import { useContext, useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { CREATE_PAGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import {
	EDITOR_ONBOARDING_BLUEPRINT_KEY,
	EDITOR_ONBOARDING_BLUEPRINT_KEY_LIVE_DOCS_ENABLED,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

import type { Creatable, CanHandle, Handle } from '../createContentFromTemplateTypes';
import { processDraftUrl } from '../helpers';
import { GetPersonalSpaceKeyQuery } from '../queries/GetPersonalSpaceKeyQuery.graphql';
import type { GetPersonalSpaceKeyQuery as GetPersonalSpaceKeyQueryType } from '../queries/__types__/GetPersonalSpaceKeyQuery';
import { useExperimentalPublishPageByBlueprintMutation } from '../queries/useExperimentalPublishPageByBlueprintMutation';

export const useEditorOnboardingBlueprintCreatable = (): Creatable => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isLicensed } = useSessionData();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [createFromBlueprintMutation] = useExperimentalPublishPageByBlueprintMutation();

	// this has to be done via getApolloClient() because neither useQuery or useLazyQuery return a promise with the data, they return void.
	const getPersonalSpaceKey = useCallback(async () => {
		let personalSpaceData;
		if (isLicensed) {
			try {
				// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
				personalSpaceData = (
					await getApolloClient().query<GetPersonalSpaceKeyQueryType>({
						query: GetPersonalSpaceKeyQuery,
					})
				).data;
			} catch (personalSpaceError) {
				markErrorAsHandled(personalSpaceError);
				getMonitoringClient().submitError(personalSpaceError, {
					attribution: Attribution.FLYWHEEL_ACTIVATION,
				});

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'failed',
						actionSubject: 'getPersonalSpaceKey',
						actionSubjectId: 'editorOnboardingTutorial',
						source: 'useEditorOnboardingBluePrintCreatable',
					},
				}).fire();
			}
		}

		const hasPersonalSpace = personalSpaceData?.user?.confluence?.hasPersonalSpace;
		const personalSpaceKey = hasPersonalSpace
			? personalSpaceData?.user?.confluence?.space?.key
			: undefined;

		return { hasPersonalSpace, personalSpaceKey };
	}, [isLicensed, createAnalyticsEvent]);

	const canHandle: CanHandle = useCallback((template) => {
		return (
			template.blueprintModuleCompleteKey === EDITOR_ONBOARDING_BLUEPRINT_KEY ||
			template.blueprintModuleCompleteKey === EDITOR_ONBOARDING_BLUEPRINT_KEY_LIVE_DOCS_ENABLED
		);
	}, []);

	const handle: Handle = useCallback(
		async (template, spaceKey, options = {}) => {
			const { title, parentPageId, subType } = options;
			/*
			 * This will create the draft under the root directory of the user's personal space.
			 * Fallback to current spaceKey (should be rare unless GetPersonalSpaceKeyQuery fails or
			 * when user immediately clicks on Editor Tutorial Template before loading is done).
			 */
			const { personalSpaceKey } = await getPersonalSpaceKey();
			const createFromBlueprintResponse = await createFromBlueprintMutation({
				variables: {
					spaceKey: personalSpaceKey || spaceKey,
					title,
					subType,
					parentPageId: personalSpaceKey ? undefined : parentPageId,
					blueprint: JSON.stringify(template),
					attemptSpaUrlResponse: false,
				},
			});

			const draftUrl =
				createFromBlueprintResponse?.data?.experimentalPublishPageByBlueprint?.links?.draftUrl;
			window.open(processDraftUrl(draftUrl), '_blank', 'noreferrer');

			experienceTracker.succeed({
				name: CREATE_PAGE_EXPERIENCE,
			});
			return true;
		},
		[createFromBlueprintMutation, experienceTracker, getPersonalSpaceKey],
	);

	return useMemo(() => ({ canHandle, handle }), [canHandle, handle]);
};
